import timezones from 'google-timezones-json';
import {IANAZone} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {noShortcutsMapping} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {HandlerOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {KeyboardProvider} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardProvider';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {WeekdaysEnum} from '../../../helpers/weekday';
import {LocaleProps} from './localeContext';

/*
 * Props.
 */

export interface LocaleSettingsProps {
  locale: LocaleProps;
  onChangeLocale: HandlerOf<LocaleProps>;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  padding: 4px;
  display: grid;
  grid-column-gap: 7px;
  grid-template-columns: 1fr auto;
`;

const StyledTimeFormatWrapperDiv = styled.div`
  margin-top: 4px;
`;

const StyledTimezoneDiv = styled.div`
  flex-grow: 0;

  > div {
    padding: 0px;
  }
`;

/*
 * Component.
 */

export const LocaleSettings: FC<LocaleSettingsProps> = (props) => (
  <StyledDiv>
    <StyledTimeFormatWrapperDiv>
      <CheckBox
        isChecked={props.locale.is24HourTime}
        onChange={(is24HourTime) =>
          props.onChangeLocale({
            ...props.locale,
            is24HourTime,
            weekStartDay: is24HourTime ? WeekdaysEnum.MONDAY : WeekdaysEnum.SUNDAY,
          })
        }
        size={VisualSizesEnum.SMALL}
      >
        24hr format
      </CheckBox>
    </StyledTimeFormatWrapperDiv>
    <StyledTimezoneDiv>
      <DropdownBoxCoordinator
        value={renderShortTimezone(props.locale.timezone)}
        size={VisualSizesEnum.SMALL}
        renderDropdown={(popoverProps) => renderTimezoneDropdown(popoverProps, props)}
      />
    </StyledTimezoneDiv>
  </StyledDiv>
);

/*
 * Helpers.
 */

function renderTimezoneDropdown(popoverProps: PopoverTransferProps, props: LocaleSettingsProps) {
  return (
    <KeyboardProvider mapping={noShortcutsMapping}>
      <SimpleDropdown
        width={300}
        maxHeight={300}
        anchor={popoverProps.anchor}
        onRequestClose={popoverProps.onRequestClose}
      >
        {Object.entries(timezones).map(([timezone, displayName]) => {
          const isChecked = timezone === props.locale.timezone;
          return (
            <SimpleDropdownItem
              isChecked={isChecked}
              key={timezone}
              onSelect={() =>
                props.onChangeLocale({
                  ...props.locale,
                  timezone,
                })
              }
            >
              {displayName}
            </SimpleDropdownItem>
          );
        })}
      </SimpleDropdown>
    </KeyboardProvider>
  );
}

function renderShortTimezone(timezone: string) {
  return IANAZone.create(timezone).offsetName(Date.now(), {format: 'short'});
}
